import React, { useState, useCallback, useContext, useEffect } from 'react';
import { Handle, Position, useReactFlow, useNodes } from 'reactflow';
import { ExclamationTriangleIcon, Pencil1Icon, Pencil2Icon } from '@radix-ui/react-icons';
import { PiSolarPanelDuotone } from "react-icons/pi";
import './SolarGenerationNode.css'; // Import the CSS file
import SolarGenerationConfigModal from './SolarGenerationConfigModal';
import EditGenerationProfileModal from '../../EditGenerationProfileModal/EditGenerationProfileModal';
import { usePipeline } from '../../../context/PipelineContext';
import { NodeMappingContext } from '../../Canvas/Canvas';
import { getDocumentById } from '../../../api/api';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';


const SolarGenerationNode = ({ id, data, selected }) => {
  // Extract data properties, ensuring we handle both direct properties and nested ones
  const { name, deviceInfo, file_path, btmFlag } = data;
  // Get file path from data or node_params
  const filePath = file_path || (data.node_params && data.node_params[0] && data.node_params[0].file_path);
  // Resolve BTM flag from data or node_params for backward compatibility
  const effectiveBtmFlag =
    typeof btmFlag === 'boolean'
      ? btmFlag
      : (data.node_params &&
         data.node_params[0] &&
         typeof data.node_params[0].Btm_flag === 'boolean'
           ? data.node_params[0].Btm_flag
           : false);
  // Get device info if available
  const [deviceData, setDeviceData] = useState(deviceInfo || data.deviceInfo);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProfileEditOpen, setIsProfileEditOpen] = useState(false);
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [isLoadingDocument, setIsLoadingDocument] = useState(false);
  const reactFlowInstance = useReactFlow();
  const nodes = useNodes();
  const { addSolarGenerationNode, updateSelectedNode } = usePipeline();
  const { updateNodeMapping } = useContext(NodeMappingContext);
  
  // Check if node is configured properly
  const isConfigured = data.isConfigured || data.isApiConfigured;

  const profileObjectId = filePath
    ? filePath.split('/').pop().replace('.csv', '')
    : (deviceData?._id || null);

  // Log the data received by the component for debugging
  useEffect(() => {
    console.log(`SolarGenerationNode ${id} received data:`, data);
  }, [id, data]);

  // Fetch document information if file_path is available
  useEffect(() => {
    const fetchDocumentInfo = async () => {
      if (filePath && isConfigured && !deviceData) {
        try {
          setIsLoadingDocument(true);
          // Extract the object ID from the file path
          // Example: './gen_upload/682d8f66c9320d321cc18b4d.csv' -> '682d8f66c9320d321cc18b4d'
          const objectId = filePath.split('/').pop().replace('.csv', '');
          console.log(`Extracting object ID from file path: ${filePath} -> ${objectId}`);
          
          if (objectId) {
            const documentData = await getDocumentById(objectId);
            if (documentData) {
              console.log(`Retrieved document data for solar node ${id}:`, documentData);
              
              // Format the document data for display
              const formattedDeviceData = {
                _id: documentData._id || objectId,
                makeAndModel: documentData.makeAndModel,
                location: documentData.location,
                sub_location: documentData.sub_location,
                poe: documentData.poe,
                maxRange: documentData.maxRange,
                startDate: documentData.start_Date,
                endDate: documentData.end_Date,
                start_Date: documentData.start_Date,
                end_Date: documentData.end_Date,
                interval: documentData.interval,
                type: documentData.type || 'solar',
                is_test: documentData.is_test !== false,
              };
              
              setDeviceData(formattedDeviceData);
              
              // Update the node data in ReactFlow to include the device info
              reactFlowInstance.setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        deviceInfo: formattedDeviceData
                      }
                    };
                  }
                  return node;
                })
              );
            }
          }
        } catch (error) {
          console.error(`Error fetching document for solar node ${id}:`, error);
        } finally {
          setIsLoadingDocument(false);
        }
      }
    };
    
    fetchDocumentInfo();
  }, [id, filePath, isConfigured, deviceData, reactFlowInstance]);
  


  // Get the current node position
  const getNodePosition = useCallback(() => {
    const node = nodes.find(n => n.id === id);
    return node ? node.position : { x: 0, y: 0 };
  }, [nodes, id]);

  const openConfigModal = useCallback(() => {
    // Allow opening modal for both initial configure and edit
    setIsModalOpen(true);
  }, []);

  const closeConfigModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const openProfileEditModal = useCallback(() => {
    setIsProfileEditOpen(true);
  }, []);

  const closeProfileEditModal = useCallback(() => {
    setIsProfileEditOpen(false);
  }, []);

  const handleProfileSaved = useCallback((updatedDoc) => {
    const formattedDeviceData = {
      _id: updatedDoc._id || profileObjectId,
      makeAndModel: updatedDoc.makeAndModel,
      location: updatedDoc.location,
      sub_location: updatedDoc.sub_location,
      poe: updatedDoc.poe,
      maxRange: updatedDoc.maxRange,
      startDate: updatedDoc.start_Date || updatedDoc.startDate,
      endDate: updatedDoc.end_Date || updatedDoc.endDate,
      start_Date: updatedDoc.start_Date || updatedDoc.startDate,
      end_Date: updatedDoc.end_Date || updatedDoc.endDate,
      interval: updatedDoc.interval,
      type: updatedDoc.type || 'solar',
      is_test: updatedDoc.is_test !== false,
    };
    setDeviceData(formattedDeviceData);
    reactFlowInstance.setNodes((nds) =>
      nds.map((node) => {
        if (node.id === id) {
          return {
            ...node,
            data: {
              ...node.data,
              deviceInfo: formattedDeviceData,
            },
          };
        }
        return node;
      })
    );
  }, [id, profileObjectId, reactFlowInstance]);

  const handleConfigured = useCallback(async (configData) => {
    setIsConfiguring(true);
    try {
      const editing = isConfigured === true;
      const { name } = configData;
      const position = getNodePosition();

      if (editing) {
        const nodePath = configData.file_path || filePath;
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              return {
                ...node,
                data: {
                  ...node.data,
                  file_path: nodePath,
                  btmFlag: configData.btmFlag,
                  deviceInfo: undefined,
                  isApiConfigured: true
                }
              };
            }
            return node;
          })
        );
        setDeviceData(undefined);
        setIsConfiguring(false);
        closeConfigModal();
        return;
      }

      // Initial configuration flow
      console.log(`Configuring solar generation node ${id} -> ${name}:`, { configData, position });
      updateNodeMapping(id, name);
      const nodePath = configData.file_path || `./gen_upload/${name}.csv`;
      addSolarGenerationNode(name, null, { x: position.x, y: position.y }, nodePath, configData.btmFlag);

      setTimeout(() => {
        reactFlowInstance.setNodes((nodes) =>
          nodes.map((node) => {
            if (node.id === id) {
              const updatedNode = {
                ...node,
                id: name,
                data: {
                  ...node.data,
                  name,
                  deviceInfo: configData.deviceInfo,
                  file_path: nodePath,
                  btmFlag: configData.btmFlag,
                  isApiConfigured: true
                }
              };
              return updatedNode;
            }
            return node;
          })
        );
        updateSelectedNode(name);
        setTimeout(async () => {
          try {
            const objectId = (configData.file_path || nodePath).split('/').pop().replace('.csv', '');
            const documentData = await getDocumentById(objectId);
            if (documentData) {
              const formattedDeviceData = {
                _id: documentData._id || objectId,
                makeAndModel: documentData.makeAndModel,
                location: documentData.location,
                sub_location: documentData.sub_location,
                poe: documentData.poe,
                maxRange: documentData.maxRange,
                startDate: documentData.start_Date,
                endDate: documentData.end_Date,
                start_Date: documentData.start_Date,
                end_Date: documentData.end_Date,
                interval: documentData.interval,
                type: documentData.type || 'solar',
                is_test: documentData.is_test !== false,
              };
              setDeviceData(formattedDeviceData);
              reactFlowInstance.setNodes((nodes) =>
                nodes.map((node) => {
                  if (node.id === name) {
                    return { ...node, data: { ...node.data, deviceInfo: formattedDeviceData } };
                  }
                  return node;
                })
              );
            }
          } catch (error) {
            console.error(`Error fetching document for newly configured solar node:`, error);
          } finally {
            setIsConfiguring(false);
            closeConfigModal();
          }
        }, 100);
      }, 100);
    } catch (error) {
      console.error('Error configuring solar generation node:', error);
      notifyError(error, { operation: isConfigured ? OP.node.editSolarGeneration : OP.node.appendSolarGeneration });
      setIsConfiguring(false);
      closeConfigModal();
    }
  }, [id, isConfigured, filePath, reactFlowInstance, getNodePosition, closeConfigModal, updateNodeMapping, addSolarGenerationNode, updateSelectedNode]);

  return (
    <>
      <div
        className={`solar-node ${selected ? 'solar-selected' : ''} ${isConfigured ? 'solar-configured' : 'solar-not-configured'}`}
      >
        <div className={`solar-node-header ${isConfigured ? 'solar-configured' : 'solar-not-configured'}`}>
          <div className="solar-node-header-title">
            <div className="solar-node-type">Solar Generation</div>
            {name && isConfigured && <div className="solar-node-name-display">{name}</div>}
          </div>
          <div className="solar-node-header-icons">
            {!isConfigured && (
              <button 
                onClick={openConfigModal}
                className="solar-config-button"
                title="Configure Solar Generation Node"
                disabled={isConfiguring}
              >
                <ExclamationTriangleIcon className="solar-icon solar-warning-icon" />
              </button>
            )}
            {isConfigured && (
              <>
                <button 
                  onClick={openConfigModal}
                  className="solar-config-button"
                  title="Edit Solar Generation Node"
                  disabled={isConfiguring}
                >
                  <Pencil1Icon className="solar-icon" />
                </button>
                {profileObjectId && (
                  <button
                    onClick={openProfileEditModal}
                    className="solar-config-button"
                    title="Edit uploaded profile metadata"
                    disabled={isConfiguring || isLoadingDocument}
                  >
                    <Pencil2Icon className="solar-icon" />
                  </button>
                )}
              </>
            )}
            <PiSolarPanelDuotone className="solar-icon" />
          </div>
        </div>
        <div className="solar-node-content">
          <div className="solar-text-xs">
            {!isConfigured && (
              <div className="solar-text-warning">
                Parameters not configured
              </div>
            )}
            {isConfiguring && (
              <div className="solar-text-info">
                Configuring...
              </div>
            )}
            {isConfigured && (
              <>
                {isLoadingDocument && (
                  <div className="solar-text-info">
                    Loading document info...
                  </div>
                )}
                {deviceData && (
                  <>
                    <div className="solar-node-device-info">
                      <div>Data: {deviceData.is_test === false ? 'Actual' : 'Test'}</div>
                      <div>Make: {deviceData.makeAndModel}</div>
                      <div>Location: {deviceData.location}</div>
                      {deviceData.sub_location && <div>Sub-location: {deviceData.sub_location}</div>}
                      {deviceData.poe && <div>PoE: {deviceData.poe}</div>}
                      <div>Range: {deviceData.maxRange}</div>
                      <div>Mode: {effectiveBtmFlag ? 'BTM' : 'Non-BTM'}</div>
                      {deviceData.startDate && <div>Start: {deviceData.startDate}</div>}
                      {deviceData.endDate && <div>End: {deviceData.endDate}</div>}
                      {deviceData.interval && <div>Interval: {deviceData.interval === 15 || deviceData.interval === '15' ? '15 mins' : 
                                                            deviceData.interval === 60 || deviceData.interval === '60' ? '1 hr' : 
                                                            `${deviceData.interval} mins`}</div>}
                    </div>
                  </>
                )}
                {!deviceData && !isLoadingDocument && filePath && (
                  <div className="solar-text-info">
                    File: {filePath.split('/').pop()}
                  </div>
                )}
              </>
            )}
          </div>
          <div className="solar-flex-container">
            {/* Only output handle, no input handle */}
            <Handle
              type="source"
              position={Position.Right}
              id="output"
              className={`solar-connection-point ${isConfigured ? 'solar-configured-output' : 'solar-not-configured'}`}
              isConnectable={isConfigured} // Only allow connections if the node is configured
              data-maxconnections="1" // Custom attribute to indicate this handle should only have one connection
            />
          </div>
        </div>
      </div>

      <SolarGenerationConfigModal
        isOpen={isModalOpen}
        onClose={closeConfigModal}
        onConfigured={handleConfigured}
        initialData={{ 
          name, 
          position: getNodePosition(),
          isConfigured: isConfigured,
          btmFlag: effectiveBtmFlag,
          is_test: deviceData?.is_test !== false,
        }}
      />

      <EditGenerationProfileModal
        isOpen={isProfileEditOpen}
        onRequestClose={closeProfileEditModal}
        profile={deviceData ? { ...deviceData, _id: deviceData._id || profileObjectId } : { _id: profileObjectId, type: 'solar' }}
        onSaved={handleProfileSaved}
      />
    </>
  );
};

export default SolarGenerationNode;